/**
 * Hint Generation Utilities
 * 
 * Provides functions to generate contextual hints based on question content
 * and subject type. Hints are designed to be encouraging and educational
 * for young learners.
 * 
 * @module hintUtils
 */

/**
 * Generate a contextual hint based on question type and content
 * 
 * @param {string} question - The question text
 * @param {Array<string>} options - Array of answer options (optional, for context)
 * @param {string} correctAnswer - The correct answer (optional, for context)
 * @param {string} subject - The subject identifier (e.g., 'math', 'phonics', 'science')
 * 
 * @returns {string} A helpful, encouraging hint for the question
 * 
 * @example
 * generateHint('What is 2 + 3?', ['4', '5', '6'], '5', 'math')
 * // Returns: "Add the numbers together to find the total! ➕"
 */
export const generateHint = (question, options, correctAnswer, subject) => {
  const questionLower = question.toLowerCase();
  const correctLower = correctAnswer ? correctAnswer.toLowerCase() : '';
  
  // Science-specific hints
  if (subject === 'science') {
    if (questionLower.includes('water') && questionLower.includes('ice')) {
      return "When water gets very cold, it changes from liquid to solid! ❄️";
    }
    if (questionLower.includes('plant') && questionLower.includes('underground')) {
      return "The part that grows underground helps the plant stay in place and drink water! 🌱";
    }
    if (questionLower.includes('water') && questionLower.includes('moving') && questionLower.includes('plant')) {
      return "This is how plants 'breathe' and move water from roots to leaves! 💧🌿";
    }
    if (questionLower.includes('photosynthesis')) {
      return "Plants use sunlight to make their own food! 🌱☀️";
    }
    if (questionLower.includes('transpiration')) {
      return "This is how plants 'breathe' and move water! 💧🌿";
    }
    if (questionLower.includes('system')) {
      return "Think about parts that work together to do something! ⚙️";
    }
    if (questionLower.includes('weather')) {
      return "Think about what happens in the sky - rain, sun, clouds! ☁️";
    }
    if (questionLower.includes('animal')) {
      return "Think about what animals need to live and grow! 🐾";
    }
  }
  
  // Math-specific hints
  if (subject === 'math') {
    if (questionLower.includes('addition') || questionLower.includes('plus') || questionLower.includes('+')) {
      return "Add the numbers together to find the total! ➕";
    }
    if (questionLower.includes('subtraction') || questionLower.includes('minus') || questionLower.includes('-')) {
      return "Take away the smaller number from the bigger one! ➖";
    }
    if (questionLower.includes('tens') || questionLower.includes('ones')) {
      return "Tens are groups of 10, ones are single numbers! 🔢";
    }
    if (questionLower.includes('bigger') || questionLower.includes('greater')) {
      return "Compare the numbers - which one is larger? 📈";
    }
    if (questionLower.includes('smaller') || questionLower.includes('less')) {
      return "Compare the numbers - which one is smaller? 📉";
    }
  }
  
  // Phonics-specific hints
  if (subject === 'phonics') {
    if (questionLower.includes('th')) {
      return "TH makes a special sound - put your tongue between your teeth! 👅";
    }
    if (questionLower.includes('sh')) {
      return "SH makes a quiet sound like 'shhh' - finger to lips! 🤫";
    }
    if (questionLower.includes('ch')) {
      return "CH sounds like a train - 'choo choo'! 🚂";
    }
    if (questionLower.includes('wh')) {
      return "WH sounds like you're asking 'what?' - blow air! 💨";
    }
    if (questionLower.includes('ck')) {
      return "CK comes at the end and sounds like a clock 'tick tock'! ⏰";
    }
  }
  
  // Reading-specific hints
  if (subject === 'reading') {
    if (questionLower.includes('character')) {
      return "Characters are the people or animals in the story! 👥";
    }
    if (questionLower.includes('setting')) {
      return "Setting is WHERE and WHEN the story happens! 📍";
    }
    if (questionLower.includes('problem')) {
      return "The problem is what goes wrong in the story! 😰";
    }
    if (questionLower.includes('solution')) {
      return "The solution is how the problem gets fixed! ✅";
    }
  }
  
  // Citizenship-specific hints
  if (subject === 'social') {
    if (questionLower.includes('citizen') || questionLower.includes('community')) {
      return "Think about being helpful and kind to others! 🤝";
    }
    if (questionLower.includes('respect') || questionLower.includes('kind')) {
      return "Being respectful means treating others nicely! 😊";
    }
    if (questionLower.includes('help') || questionLower.includes('care')) {
      return "Good citizens help others who need it! 💪";
    }
  }
  
  // Skip counting hints
  if (subject === 'skipcounting') {
    if (questionLower.includes('2')) {
      return "Count by 2s: 2, 4, 6, 8, 10... like counting pairs! 👥";
    }
    if (questionLower.includes('5')) {
      return "Count by 5s: 5, 10, 15, 20... like counting fingers! ✋";
    }
    if (questionLower.includes('10')) {
      return "Count by 10s: 10, 20, 30, 40... like counting toes! 🦶";
    }
  }
  
  // Fallback hints by subject
  const fallbackHints = {
    phonics: "Say the word slowly and listen to each sound! 👂",
    math: "Think step by step - what do you need to do? 🤔",
    reading: "Look for clues in the question! 🔍",
    science: "Think about what you know about nature! 🌿",
    social: "Think about being a good person! 😊",
    skipcounting: "Count by the number mentioned! 🔢"
  };
  
  return fallbackHints[subject] || "Think carefully about what you know! 💭";
};

